/**
 * Tenant provisioning wizard — Wave 1a / W1a-19.
 *
 * 7-step flow:
 *   1. Basic       — name, slug, orgCode, mode
 *   2. SSO         — provider + optional config (skippable)
 *   3. Settings    — the two tenant settings (web search, external connections)
 *   4. Compliance  — radio: SOC2 / ISO27001 / ITAR / FedRAMP / HIPAA / None
 *   5. Resources   — maxUsers / maxStorageGb / maxMonthlyTokens
 *   6. First admin — email + first/last name (skippable)
 *   7. Review      — full payload summary
 *
 * On submit → call useProvisionTenant. The success screen shows the
 * single-use invitation token + invite URL ONCE — operator must capture
 * it from the dialog before closing (token is not refetchable).
 *
 * Reused by the install /setup/<token> page — when `embedded=true` the
 * outer Dialog is skipped and the wizard renders inline; the parent
 * controls submission flow.
 */

import {
  useState,
  useId,
  isValidElement,
  cloneElement,
  type ReactNode,
  type ReactElement,
} from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Copy,
  CheckCircle2,
  AlertTriangle,
} from 'lucide-react';
import {
  useProvisionTenant,
  type ProvisionTenantBody,
  type ProvisionTenantResponse,
  type TenantMode,
  type ComplianceProfile,
  type SsoProvider,
} from '@/lib/use-tenant-provisioning';

// ─── The two tenant settings ─────────────────────────────────────────────────
//
// Foundations 1 / B0-T1. The wizard used to set eight feature toggles here;
// the founder abolished feature flags on 2026-09-08 (decisions.md, "Features,
// flags and plans") and every rail is simply on. What a new tenant still has
// to choose is whether it may reach outside.
//
// The keys MUST match `TenantSettingKeySchema` in
// `packages/shared/src/schemas/ai-features.ts` — the provisioning API
// validates them strictly and 422s an unknown key.
//
// B1-T0: the STEP is called Settings now, and so is its copy — "Features" no
// longer names anything in this product (decisions.md 2026-09-08). The request
// FIELD is still `features`, deliberately: see the comment on
// `packages/api/src/routes/v1/admin/tenant-provisioning-schema.ts:99` — the
// wire name is kept so provisioning clients need not change shape in the same
// release that changed its meaning.
// BOTH DEFAULT ON — founder decision SEC-6, 2026-09-09 (decisions.md
// § Features item 10): a tenant is created with web search and external
// connections allowed, and the tenant admin owns the two switches from that
// moment on. `allow_web_search` used to default OFF here while the API's
// `defaultTenantSettings()` derived `allow_external_connections` from the
// platform air-gap posture — two rules, in two packages, for one pair of
// values. Both now say the same thing, and the wizard SENDS both explicitly so
// what the sysadmin saw on the screen is what lands in the audited rows.
const TENANT_SETTINGS: Array<{ key: string; label: string; defaultOn: boolean }> = [
  { key: 'allow_web_search', label: 'Allow web search', defaultOn: true },
  { key: 'allow_external_connections', label: 'Allow external connections', defaultOn: true },
];

const COMPLIANCE_PROFILES: Array<{ key: ComplianceProfile; label: string; description: string }> = [
  { key: 'none', label: 'None', description: 'No regulatory framework gating' },
  { key: 'soc2', label: 'SOC 2', description: 'Service Organization Control 2 — audit controls only' },
  { key: 'iso27001', label: 'ISO 27001', description: 'ISMS audit controls only' },
  { key: 'hipaa', label: 'HIPAA', description: 'PHI handling, BAA tracking, audit retention' },
  {
    key: 'itar',
    label: 'ITAR',
    description:
      'US export-control: US Person verification, opaque citations, sliding-window memory, FIPS dual-sign auto-upgrade',
  },
  {
    key: 'fedramp',
    label: 'FedRAMP',
    // Fix-wave #610 — make it explicit that FedRAMP gets ONLY the FIPS
    // signing posture and is NOT subjected to ITAR export-control
    // semantics. The earlier wording suggested both profiles were
    // equivalent FIPS upgrades, which matched the (buggy) backend
    // mapping. The backend now keeps the two separate.
    description:
      'Federal cloud baseline: FIPS dual-sign auto-upgrade only. Does NOT enable ITAR export-control flags (US Person check, opaque citations, sliding-window memory).',
  },
];

const SSO_PROVIDERS: Array<{ key: SsoProvider; label: string }> = [
  { key: 'oidc', label: 'OIDC (generic)' },
  { key: 'saml', label: 'SAML' },
  { key: 'google', label: 'Google Workspace' },
  { key: 'entra', label: 'Microsoft Entra ID' },
];

// ─── Wizard state shape ──────────────────────────────────────────────────────

interface WizardForm {
  name: string;
  slug: string;
  orgCode: string;
  mode: TenantMode;
  ssoEnabled: boolean;
  ssoProvider: SsoProvider;
  ssoClientId: string;
  ssoTenantId: string;
  ssoDiscoveryUrl: string;
  features: Record<string, boolean>;
  complianceProfile: ComplianceProfile;
  rlMaxUsers: string;
  rlMaxStorageGb: string;
  rlMaxMonthlyTokens: string;
  firstAdminEnabled: boolean;
  faEmail: string;
  faFirstName: string;
  faLastName: string;
}

const initialForm: WizardForm = {
  name: '',
  slug: '',
  orgCode: '',
  mode: 'standalone',
  ssoEnabled: false,
  ssoProvider: 'oidc',
  ssoClientId: '',
  ssoTenantId: '',
  ssoDiscoveryUrl: '',
  features: Object.fromEntries(TENANT_SETTINGS.map((f) => [f.key, f.defaultOn])),
  complianceProfile: 'none',
  rlMaxUsers: '',
  rlMaxStorageGb: '',
  rlMaxMonthlyTokens: '',
  firstAdminEnabled: false,
  faEmail: '',
  faFirstName: '',
  faLastName: '',
};

const STEP_TITLES = [
  'Basic',
  'SSO',
  'Settings',
  'Compliance',
  // Closes #617: canonical 8-step provisioning sequence places Branding
  // between Compliance and Resources. Per SYSADMIN-PLATFORM-SPEC v1.0
  // §4.7, sysadmin does NOT author tenant branding values during
  // provisioning — the new tenant inherits platform defaults. This
  // step is a read-only confirmation of what the tenant will inherit,
  // plus a pointer to the post-provision lock-matrix editor at
  // /sysadmin/tenants/:id/branding.
  'Branding',
  'Resources',
  'First Admin',
  'Review',
];

// ─── Wizard component ────────────────────────────────────────────────────────

interface ProvisionTenantWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (result: ProvisionTenantResponse) => void;
}

export function ProvisionTenantWizard({
  open,
  onOpenChange,
  onSuccess,
}: ProvisionTenantWizardProps) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState<WizardForm>(initialForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [result, setResult] = useState<ProvisionTenantResponse | null>(null);
  const provision = useProvisionTenant();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [tokenCopied, setTokenCopied] = useState(false);

  const totalSteps = STEP_TITLES.length;

  function update<K extends keyof WizardForm>(field: K, value: WizardForm[K]) {
    setForm((p) => ({ ...p, [field]: value }));
    if (errors[field as string]) {
      setErrors((p) => {
        const c = { ...p };
        delete c[field as string];
        return c;
      });
    }
  }

  function validateStep(s: number): boolean {
    const e: Record<string, string> = {};
    if (s === 1) {
      if (!form.name.trim()) e.name = 'Name is required';
      if (!form.slug.trim()) e.slug = 'Slug is required';
      else if (!/^[a-z][a-z0-9-]{2,49}$/.test(form.slug)) {
        e.slug =
          'Slug must start with a lowercase letter and use only lowercase letters, digits, hyphens (3-50 chars)';
      }
      if (!form.orgCode.trim()) e.orgCode = 'Org code is required';
      else if (!/^[A-Z][A-Z0-9-]{2,29}$/.test(form.orgCode)) {
        e.orgCode =
          'Org code must start with an uppercase letter and use only uppercase letters, digits, hyphens (3-30 chars)';
      }
    }
    if (s === 2 && form.ssoEnabled) {
      if (!form.ssoClientId.trim()) e.ssoClientId = 'Client ID required when SSO enabled';
    }
    if (s === 5) {
      if (form.rlMaxUsers && !/^\d+$/.test(form.rlMaxUsers)) e.rlMaxUsers = 'Must be a positive integer';
      if (form.rlMaxStorageGb && !/^\d+$/.test(form.rlMaxStorageGb)) e.rlMaxStorageGb = 'Must be a positive integer';
      if (form.rlMaxMonthlyTokens && !/^\d+$/.test(form.rlMaxMonthlyTokens)) e.rlMaxMonthlyTokens = 'Must be a positive integer';
    }
    if (s === 6 && form.firstAdminEnabled) {
      if (!form.faEmail.trim()) e.faEmail = 'Email required for first admin';
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.faEmail)) e.faEmail = 'Invalid email';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  function next() {
    if (!validateStep(step)) return;
    setStep((s) => Math.min(s + 1, totalSteps));
  }
  function prev() {
    setStep((s) => Math.max(1, s - 1));
  }

  function buildBody(): ProvisionTenantBody {
    const body: ProvisionTenantBody = {
      name: form.name.trim(),
      slug: form.slug.trim(),
      orgCode: form.orgCode.trim(),
      mode: form.mode,
      features: form.features,
      complianceProfile: form.complianceProfile,
    };
    if (form.ssoEnabled) {
      body.sso = {
        provider: form.ssoProvider,
        clientId: form.ssoClientId.trim() || undefined,
        tenantId: form.ssoTenantId.trim() || undefined,
        discoveryUrl: form.ssoDiscoveryUrl.trim() || undefined,
      };
    }
    const rl: ProvisionTenantBody['resourceLimits'] = {};
    if (form.rlMaxUsers) rl.maxUsers = parseInt(form.rlMaxUsers, 10);
    if (form.rlMaxStorageGb) rl.maxStorageGb = parseInt(form.rlMaxStorageGb, 10);
    if (form.rlMaxMonthlyTokens) rl.maxMonthlyTokens = parseInt(form.rlMaxMonthlyTokens, 10);
    if (Object.keys(rl).length > 0) body.resourceLimits = rl;
    if (form.firstAdminEnabled && form.faEmail) {
      body.firstAdmin = {
        email: form.faEmail.trim(),
        firstName: form.faFirstName.trim() || undefined,
        lastName: form.faLastName.trim() || undefined,
      };
    }
    return body;
  }

  async function submit() {
    if (!validateStep(step)) return;
    setSubmitError(null);
    try {
      const r = await provision.mutateAsync(buildBody());
      setResult(r);
      onSuccess?.(r);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to provision tenant');
    }
  }

  function reset() {
    setStep(1);
    setForm(initialForm);
    setErrors({});
    setResult(null);
    setSubmitError(null);
    setTokenCopied(false);
  }

  function handleClose() {
    reset();
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : handleClose())}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {result ? 'Tenant created' : 'Provision new tenant'}
          </DialogTitle>
          <DialogDescription>
            {result
              ? 'The tenant has been provisioned. Capture the invitation token below — it cannot be retrieved later.'
              : `Step ${step} of ${totalSteps}: ${STEP_TITLES[step - 1]}`}
          </DialogDescription>
        </DialogHeader>

        {!result && (
          <>
            <Progress value={(step / totalSteps) * 100} className="h-1" />

            <div className="py-4 min-h-[280px]">
              {step === 1 && <Step1Basic form={form} errors={errors} update={update} />}
              {step === 2 && <Step2Sso form={form} errors={errors} update={update} />}
              {step === 3 && <Step3Settings form={form} update={update} />}
              {step === 4 && <Step4Compliance form={form} update={update} />}
              {step === 5 && <Step5Branding />}
              {step === 6 && <Step6Resources form={form} errors={errors} update={update} />}
              {step === 7 && <Step7FirstAdmin form={form} errors={errors} update={update} />}
              {step === 8 && <Step8Review form={form} />}
            </div>

            {submitError && (
              <div className="rounded border border-destructive bg-destructive/10 px-3 py-2 text-sm text-destructive flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{submitError}</span>
              </div>
            )}

            <div className="flex justify-between gap-2">
              <Button variant="ghost" onClick={handleClose} disabled={provision.isPending}>
                Cancel
              </Button>
              <div className="flex gap-2">
                {step > 1 && (
                  <Button variant="outline" onClick={prev} disabled={provision.isPending}>
                    <ChevronLeft className="h-4 w-4 mr-1" /> Back
                  </Button>
                )}
                {step < totalSteps && (
                  <Button onClick={next}>
                    Next <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                )}
                {step === totalSteps && (
                  <Button onClick={submit} disabled={provision.isPending}>
                    {provision.isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Provisioning…
                      </>
                    ) : (
                      <>
                        <Check className="h-4 w-4 mr-2" /> Provision tenant
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </>
        )}

        {result && (
          <div className="space-y-4">
            <div className="rounded border border-emerald-500 bg-emerald-50 dark:bg-emerald-950/30 p-3 text-sm flex items-start gap-2">
              <CheckCircle2 className="h-5 w-5 text-emerald-600 mt-0.5 shrink-0" />
              <div>
                <div className="font-medium">{result.tenant.name} provisioned</div>
                <div className="text-muted-foreground text-xs mt-1">
                  ID: <code className="font-mono">{result.tenant.id}</code> · slug:{' '}
                  <code className="font-mono">{result.tenant.slug}</code> · code:{' '}
                  <code className="font-mono">{result.tenant.orgCode}</code>
                </div>
                {result.signingPolicyReconciled && (
                  <div className="text-xs mt-2 text-amber-700 dark:text-amber-400">
                    Signing policy auto-upgraded to FIPS dual-sign for ITAR/FedRAMP profile.
                  </div>
                )}
              </div>
            </div>

            {result.invitation && (
              <div className="rounded border bg-muted/30 p-3 space-y-2">
                <div className="text-sm font-medium">First admin invitation</div>
                <div className="text-xs text-muted-foreground">
                  Send this URL to the new admin to set their password. The token expires{' '}
                  {new Date(result.invitation.expiresAt).toLocaleString()}.
                </div>
                <div className="flex gap-2 items-center">
                  <code className="flex-1 text-xs font-mono bg-background border rounded px-2 py-1.5 break-all">
                    {result.invitation.inviteUrl}
                  </code>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      void navigator.clipboard.writeText(result.invitation!.inviteUrl);
                      setTokenCopied(true);
                      setTimeout(() => setTokenCopied(false), 1500);
                    }}
                  >
                    {tokenCopied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                  </Button>
                </div>
                <div className="text-xs text-amber-600 dark:text-amber-400">
                  Copy this URL now — it will not be shown again.
                </div>
              </div>
            )}

            <div className="flex justify-end">
              <Button onClick={handleClose}>Done</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── Steps ───────────────────────────────────────────────────────────────────

interface StepProps {
  form: WizardForm;
  update: <K extends keyof WizardForm>(field: K, value: WizardForm[K]) => void;
}
interface StepErrorsProps extends StepProps {
  errors: Record<string, string>;
}

function Field({
  label,
  error,
  children,
  hint,
}: {
  label: string;
  error?: string;
  children: ReactNode;
  hint?: string;
}) {
  // Wire the <Label htmlFor> to the underlying control so screen readers,
  // axe, and Playwright's `getByLabel(...)` resolve correctly. Before this
  // (cluster fix-wave-2 / #862) SYS-TENANTS-009/010/#617 timed out at
  // `page.getByLabel(/Tenant name/i)` because the Input had no id/label
  // association — the wizard's Step 1 form was effectively non-interactable
  // from accessibility tooling. We clone the child to inject `id` so callers
  // don't have to thread it manually.
  const generatedId = useId();
  const child = isValidElement(children)
    ? cloneElement(children as ReactElement<{ id?: string }>, {
        id: (children as ReactElement<{ id?: string }>).props.id ?? generatedId,
      })
    : children;
  const childId =
    (isValidElement(children)
      ? ((children as ReactElement<{ id?: string }>).props.id ?? generatedId)
      : undefined);
  return (
    <div className="space-y-1.5">
      <Label htmlFor={childId} className="text-sm">{label}</Label>
      {child}
      {hint && !error && <div className="text-xs text-muted-foreground">{hint}</div>}
      {error && <div className="text-xs text-destructive">{error}</div>}
    </div>
  );
}

function Step1Basic({ form, errors, update }: StepErrorsProps) {
  return (
    <div className="space-y-4">
      <Field label="Tenant name" error={errors.name}>
        <Input
          value={form.name}
          onChange={(e) => update('name', e.target.value)}
          placeholder="Acme Industrial"
        />
      </Field>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Slug" error={errors.slug} hint="lowercase, used in URLs (e.g. acme-industrial)">
          <Input
            value={form.slug}
            onChange={(e) => update('slug', e.target.value.toLowerCase())}
            placeholder="acme-industrial"
            className="font-mono"
          />
        </Field>
        <Field label="Org code" error={errors.orgCode} hint="UPPERCASE, used in tenant switcher">
          <Input
            value={form.orgCode}
            onChange={(e) => update('orgCode', e.target.value.toUpperCase())}
            placeholder="ACME-IND"
            className="font-mono"
          />
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Deployment mode">
          <Select value={form.mode} onValueChange={(v) => update('mode', v as TenantMode)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="standalone">Standalone</SelectItem>
              <SelectItem value="iq_connected">IQ-Connected</SelectItem>
            </SelectContent>
          </Select>
        </Field>
      </div>
    </div>
  );
}

function Step2Sso({ form, errors, update }: StepErrorsProps) {
  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2">
        <Checkbox
          id="sso-enabled"
          checked={form.ssoEnabled}
          onCheckedChange={(v) => update('ssoEnabled', !!v)}
        />
        <Label htmlFor="sso-enabled" className="cursor-pointer text-sm leading-tight">
          Configure single sign-on for this tenant
          <div className="text-xs text-muted-foreground font-normal mt-0.5">
            Skip to add later via the tenant Settings tab.
          </div>
        </Label>
      </div>
      {form.ssoEnabled && (
        <div className="space-y-3 pl-6 border-l">
          <Field label="Provider">
            <Select
              value={form.ssoProvider}
              onValueChange={(v) => update('ssoProvider', v as SsoProvider)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SSO_PROVIDERS.map((p) => (
                  <SelectItem key={p.key} value={p.key}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Client ID" error={errors.ssoClientId}>
            <Input
              value={form.ssoClientId}
              onChange={(e) => update('ssoClientId', e.target.value)}
              placeholder="abc123…"
            />
          </Field>
          <Field label="Tenant ID (Entra) / Realm" hint="Optional">
            <Input
              value={form.ssoTenantId}
              onChange={(e) => update('ssoTenantId', e.target.value)}
            />
          </Field>
          <Field label="Discovery URL" hint="OIDC well-known configuration">
            <Input
              value={form.ssoDiscoveryUrl}
              onChange={(e) => update('ssoDiscoveryUrl', e.target.value)}
              placeholder="https://idp.example.com/.well-known/openid-configuration"
            />
          </Field>
        </div>
      )}
    </div>
  );
}

function Step3Settings({ form, update }: StepProps) {
  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        Every rail and every capability is on. These two settings decide whether this
        tenant may reach outside; its admin can change either later in
        Admin → Settings → General.
      </p>
      <Separator />
      <div className="space-y-2 max-h-[280px] overflow-y-auto pr-2">
        {TENANT_SETTINGS.map((f) => (
          <div key={f.key} className="flex items-center gap-2 py-1">
            <Checkbox
              id={`f-${f.key}`}
              checked={form.features[f.key] ?? false}
              onCheckedChange={(v) =>
                update('features', { ...form.features, [f.key]: !!v })
              }
            />
            <Label htmlFor={`f-${f.key}`} className="cursor-pointer text-sm font-normal flex-1">
              {f.label}
              <span className="text-xs text-muted-foreground ml-2 font-mono">{f.key}</span>
            </Label>
          </div>
        ))}
      </div>
    </div>
  );
}

function Step4Compliance({ form, update }: StepProps) {
  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        Select a compliance profile. ITAR and FedRAMP automatically upgrade the bundle signing policy to FIPS
        dual-signature.
      </p>
      <RadioGroup
        value={form.complianceProfile}
        onValueChange={(v) => update('complianceProfile', v as ComplianceProfile)}
      >
        {COMPLIANCE_PROFILES.map((p) => (
          <div
            key={p.key}
            className="flex items-start gap-3 rounded border p-3 hover:bg-accent/30 cursor-pointer"
            onClick={() => update('complianceProfile', p.key)}
          >
            <RadioGroupItem value={p.key} id={`cp-${p.key}`} className="mt-0.5" />
            <Label htmlFor={`cp-${p.key}`} className="flex-1 cursor-pointer text-sm font-normal">
              <div className="font-medium">{p.label}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{p.description}</div>
            </Label>
          </div>
        ))}
      </RadioGroup>
    </div>
  );
}

// Closes #617: read-only Branding step — confirms the operator sees
// what platform-default branding the new tenant will inherit. Sysadmin
// does NOT author tenant brand values during provisioning (per
// SYSADMIN-PLATFORM-SPEC v1.0 §4.7). The post-provision Branding tab
// at /sysadmin/tenants/:id/branding is where the per-field lock matrix
// is set; that endpoint requires the tenant to exist, so it's
// intentionally a follow-up step rather than part of the wizard.
function Step5Branding() {
  return (
    <div className="space-y-4 text-sm">
      <div className="rounded border bg-muted/20 p-4 space-y-2">
        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Branding inheritance
        </div>
        <p className="text-sm">
          The new tenant will inherit the <strong>platform default
          branding</strong> (logo, colors, product name, login text)
          configured at{' '}
          <code className="font-mono text-xs">/sysadmin/settings/branding</code>.
        </p>
        <p className="text-xs text-muted-foreground">
          No tenant-specific brand values are set during provisioning.
          This is intentional — sysadmin sets platform defaults and
          the per-field lock matrix; tenant admins author their own
          overrides at <code className="font-mono">/admin/branding</code>.
        </p>
      </div>
      <div className="rounded border border-amber-500/30 bg-amber-500/5 p-3 text-xs flex items-start gap-2">
        <AlertTriangle className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
        <div className="space-y-1">
          <div className="font-medium text-amber-700 dark:text-amber-300">
            Locks are configured post-provision
          </div>
          <div className="text-muted-foreground">
            After the tenant is created, open{' '}
            <code className="font-mono">
              /sysadmin/tenants/:id/branding
            </code>{' '}
            to set the per-field lock matrix — which brand fields the
            tenant admin can override vs. which inherit immutably from
            the platform default.
          </div>
        </div>
      </div>
    </div>
  );
}

function Step6Resources({ form, errors, update }: StepErrorsProps) {
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Optional resource caps. Leave blank to use platform defaults.
      </p>
      <Field label="Max users" error={errors.rlMaxUsers}>
        <Input
          inputMode="numeric"
          value={form.rlMaxUsers}
          onChange={(e) => update('rlMaxUsers', e.target.value.replace(/[^\d]/g, ''))}
          placeholder="100"
        />
      </Field>
      <Field label="Max storage (GB)" error={errors.rlMaxStorageGb}>
        <Input
          inputMode="numeric"
          value={form.rlMaxStorageGb}
          onChange={(e) => update('rlMaxStorageGb', e.target.value.replace(/[^\d]/g, ''))}
          placeholder="10"
        />
      </Field>
      <Field label="Max monthly tokens" error={errors.rlMaxMonthlyTokens}>
        <Input
          inputMode="numeric"
          value={form.rlMaxMonthlyTokens}
          onChange={(e) => update('rlMaxMonthlyTokens', e.target.value.replace(/[^\d]/g, ''))}
          placeholder="1000000"
        />
      </Field>
    </div>
  );
}

function Step7FirstAdmin({ form, errors, update }: StepErrorsProps) {
  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2">
        <Checkbox
          id="fa-enabled"
          checked={form.firstAdminEnabled}
          onCheckedChange={(v) => update('firstAdminEnabled', !!v)}
        />
        <Label htmlFor="fa-enabled" className="cursor-pointer text-sm leading-tight">
          Invite the first tenant admin now
          <div className="text-xs text-muted-foreground font-normal mt-0.5">
            We'll create an invited user + send an invitation token (you'll see it after submit).
          </div>
        </Label>
      </div>
      {form.firstAdminEnabled && (
        <div className="space-y-3 pl-6 border-l">
          <Field label="Email" error={errors.faEmail}>
            <Input
              type="email"
              value={form.faEmail}
              onChange={(e) => update('faEmail', e.target.value)}
              placeholder="admin@acme.com"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="First name (optional)">
              <Input
                value={form.faFirstName}
                onChange={(e) => update('faFirstName', e.target.value)}
              />
            </Field>
            <Field label="Last name (optional)">
              <Input
                value={form.faLastName}
                onChange={(e) => update('faLastName', e.target.value)}
              />
            </Field>
          </div>
        </div>
      )}
    </div>
  );
}

function Step8Review({ form }: { form: WizardForm }) {
  const settingsOn = Object.entries(form.features)
    .filter(([, v]) => v)
    .map(([k]) => k);
  return (
    <div className="space-y-3 text-sm">
      <Section title="Basic">
        <KV k="Name" v={form.name} />
        <KV k="Slug" v={form.slug} mono />
        <KV k="Org code" v={form.orgCode} mono />
        <KV k="Mode" v={form.mode} />
      </Section>
      <Section title="SSO">
        {form.ssoEnabled ? (
          <>
            <KV k="Provider" v={form.ssoProvider} />
            <KV k="Client ID" v={form.ssoClientId || '—'} mono />
            {form.ssoTenantId && <KV k="Tenant ID" v={form.ssoTenantId} mono />}
            {form.ssoDiscoveryUrl && <KV k="Discovery URL" v={form.ssoDiscoveryUrl} mono />}
          </>
        ) : (
          <KV k="" v="Skipped" />
        )}
      </Section>
      <Section title={`Settings (${settingsOn.length} on)`}>
        <div className="text-xs text-muted-foreground">
          {settingsOn.length === 0 ? 'None' : settingsOn.join(', ')}
        </div>
      </Section>
      <Section title="Compliance">
        <KV k="Profile" v={form.complianceProfile} />
        {(form.complianceProfile === 'itar' || form.complianceProfile === 'fedramp') && (
          <div className="text-xs text-amber-600 dark:text-amber-400">
            Signing policy will auto-upgrade to FIPS dual-sign.
          </div>
        )}
      </Section>
      <Section title="Branding">
        <div className="text-xs text-muted-foreground">
          Inherits platform defaults. Per-field lock matrix set
          post-provision at <code className="font-mono">/sysadmin/tenants/:id/branding</code>.
        </div>
      </Section>
      <Section title="Resource limits">
        <KV k="Max users" v={form.rlMaxUsers || '—'} />
        <KV k="Max storage (GB)" v={form.rlMaxStorageGb || '—'} />
        <KV k="Max monthly tokens" v={form.rlMaxMonthlyTokens || '—'} />
      </Section>
      <Section title="First admin">
        {form.firstAdminEnabled ? (
          <>
            <KV k="Email" v={form.faEmail} />
            {(form.faFirstName || form.faLastName) && (
              <KV k="Name" v={`${form.faFirstName} ${form.faLastName}`.trim()} />
            )}
          </>
        ) : (
          <KV k="" v="Skipped" />
        )}
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="rounded border bg-muted/20 p-3">
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-2">
        {title}
      </div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function KV({ k, v, mono }: { k: string; v: string; mono?: boolean }) {
  return (
    <div className="flex justify-between gap-2 text-sm">
      <span className="text-muted-foreground">{k}</span>
      <span className={mono ? 'font-mono' : ''}>{v}</span>
    </div>
  );
}
